<template>
  <div class="addUser">
    <Loading v-if="loading" />
    <div class="addUser__header">
      <Link :title="$t('backToUsers')" @back-to="backTo" />
      <Title :title="$t('addUser')" />
    </div>
    <div class="addUser__body">
      <FormKit
        id="addUser-form"
        v-model="formData"
        type="form"
        :actions="false"
        :config="validationRules"
        @submit="submitHandler"
      >
        <DisableAutocomplete />
        <Input
          name="email"
          :label="$t('email')"
          :type="fieldTypes.email"
          validation="required|email"
          :validation-messages="{
            required: $t('validation.required'),
            email: $t('validation.email')
          }"
          data-testid="inputEmail"
        />
        <Dropdown
          class="addUser__body-dropdown"
          :label="$t('role')"
          :selected-option="userRoleDropdown.selectedOption"
          :options="userRoleDropdown.options"
          data-testid="dropdownUserRole"
          @select-option="selectUser"
        />
        <Checkbox
          class="addUser__body-checkbox"
          name="webToolAccess"
          :value="formData.webToolAccess"
          :title="$t('webToolAccess')"
          @change="selectWebToolAccess"
        />
        <Checkbox
          class="addUser__body-checkbox"
          name="boxAccess"
          :value="formData.boxAccess"
          :title="$t('boxAccess')"
          @change="selectBoxAccess"
        />
      </FormKit>
    </div>
  </div>
  <Footer @clean-form="cleanForm" @submit-form="submitForm" />
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { mapActions, mapState } from 'vuex';

import Loading from '@/views/Loading.vue';

import router from '@/router';

import { infoFromUser } from '@/constants';

import { Option } from '@/types';
import { PharmacyAccessesAdd } from '@/store/modules/users/types';
import { AppRole } from '@/store/modules/roles/types';

@Options({
  name: 'AddUser',
  components: {
    Loading
  },
  data() {
    return {
      formData: {
        email: '',
        roleName: '',
        pharmacyId: '',
        webToolAccess: true,
        boxAccess: true
      },
      userRoleDropdown: {
        selectedOption: {
          value: '',
          label: ''
        },
        options: []
      },
      fieldTypes: {
        email: 'text'
      },
      validationRules: {},
      infoFromUser,
      loading: false
    };
  },
  created() {
    this.show();
  },
  computed: {
    ...mapState('roles', ['appRoles']),
    ...mapState('account', ['user'])
  },
  methods: {
    ...mapActions('users', ['addPharmacyAccesses']),
    ...mapActions('roles', ['getAppRoles']),
    async show() {
      await this.getAppRoles();

      this.initUserRole();
    },
    async submitHandler(data: PharmacyAccessesAdd) {
      this.loading = true;

      const payload = {
        ...data,
        roleName: this.userRoleDropdown.selectedOption.value,
        pharmacyId: this.user.pharmacyId
      };
      await this.addPharmacyAccesses(payload);
      this.$formkit.reset('addUser-form');

      router.push({ name: 'Users' });

      this.loading = false;
    },
    submitForm() {
      this.$formkit.submit('addUser-form');
    },
    cleanForm() {
      this.$formkit.reset('addUser-form');
    },
    backTo() {
      router.push({ name: 'Users' });
    },
    initUserRole() {
      const roles: Record<string, unknown> = {
        main_user: this.$t('mainUser'),
        user: this.$t('user')
      };

      this.userRoleDropdown.options = this.appRoles.map((i: AppRole) => ({
        value: i.value,
        label: roles[i.value]
      }));

      this.userRoleDropdown.selectedOption = this.userRoleDropdown.options.find(
        (i: Option) => i.value === this.user.role.name
      );
    },
    selectUser(option: Option) {
      this.userRoleDropdown.selectedOption = option;
      this.formData.role = option.value;
    },
    selectWebToolAccess() {
      this.formData.webToolAccess = !this.formData.webToolAccess;
    },
    selectBoxAccess() {
      this.formData.boxAccess = !this.formData.boxAccess;
    }
  }
})
export default class AddUserComponent extends Vue {}
</script>

<style lang="scss">
.addUser {
  height: calc(100vh - 40px);
  padding: 20px;
  padding-bottom: 0;
  overflow-y: scroll;
  overflow-x: hidden;
  .addUser__header {
    .title {
      margin-top: 27px;
    }
  }
  .addUser__body {
    padding-top: 32px;
    .formkit-form {
      display: flex;
      flex-direction: column;
      .input {
        width: 377px;
      }
      .addUser__body-dropdown {
        width: 377px;
        height: 48px;
        margin-bottom: 30px;
      }
      .addUser__body-checkbox {
        margin-bottom: 14px;
        .checkbox-label {
          cursor: auto;
        }
      }
    }
    .formkit-outer {
      width: 377px;
      .formkit-options {
        display: flex;
        .formkit-option {
          margin: 0 20px;
        }
      }
    }
  }
}
</style>
